import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Users, BookOpen, Clock, LogIn, Timer, GraduationCap, Filter, Calendar, Download, Search, Eye, AlertTriangle, X } from "lucide-react";
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { format, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { formatDateStandard } from "@servify/shared-utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Analytics data types
interface WelcomeModuleLoginRate {
  milestone: string;
  loginPercentage: number;
  usersAccessed: number;
  totalUsers: number;
  userType: 'candidate' | 'employee';
}

interface ModuleCompletion {
  module: string;
  completionPercentage: number;
  completed: number;
  total: number;
  sequence: number;
}


interface ScoreDistribution {
  score: number;
  count: number;
  percentage: number;
}

interface QuizModuleAverage {
  moduleTitle: string;
  averageScore: number;
  totalAttempts: number;
  passingRate: number;
}

interface RecentActivity {
  timestamp: string | Date;
  action?: string;
  userName?: string;
  moduleTitle?: string;
}

export default function Analytics() {
  const { toast } = useToast();
  
  // Filter state for targeted analytics
  const [filters, setFilters] = useState({
    dateRange: 30, // Default 30 days
    customDateRange: undefined as DateRange | undefined,
    department: undefined as string | undefined
  });
  
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Enhanced analytics state
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [performanceFilter, setPerformanceFilter] = useState('all');
  const [progressFilter, setProgressFilter] = useState('all');
  const [quizFilter, setQuizFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedView, setSelectedView] = useState<'overview' | 'user-details'>('overview');
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: ['analytics', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      // Use custom date range if available, otherwise use the preset dateRange
      if (filters.customDateRange?.from && filters.customDateRange?.to) {
        params.append('startDate', filters.customDateRange.from.toISOString());
        params.append('endDate', filters.customDateRange.to.toISOString());
        // Don't send dateRange when using custom range
      } else {
        params.append('dateRange', filters.dateRange.toString());
        // Don't send startDate/endDate when using preset range
      }
      
      if (filters.department) params.append('department', filters.department);
      
      const response = await apiRequest("GET", `/analytics?${params}`);
      const data = await response.json();
      return data.analytics;
    },
  });

  // Fetch detailed user analytics for enhanced views
  const { data: detailedAnalyticsData, isLoading: isLoadingDetailed } = useQuery({
    queryKey: ['detailed-analytics', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.customDateRange?.from && filters.customDateRange?.to) {
        params.append('startDate', filters.customDateRange.from.toISOString());
        params.append('endDate', filters.customDateRange.to.toISOString());
      } else {
        params.append('dateRange', filters.dateRange.toString());
      }
      if (filters.department) params.append('department', filters.department);
      // Add cache-busting parameter
      params.append('_t', Date.now().toString());

      const response = await apiRequest("GET", `/analytics/detailed?${params}`);
      const result = await response.json();
      
      // Ensure we return an array
      let data = result.data;
      if (!Array.isArray(data)) {
        console.error('Invalid detailed analytics data format:', result);
        // Return empty array as fallback
        return [];
      }
      
      return data;
    },
    enabled: selectedView === 'user-details'
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="analytics">
        <div>
          <h1 className="text-3xl font-bold text-servify-dark-grey">Analytics Dashboard</h1>
          <p className="text-servify-medium-grey">Loading analytics data...</p>
        </div>
        
        {/* Simple loading state filters */}
        <div className="flex gap-4 p-4 bg-servify-white-grey rounded-lg">
          <div className="text-servify-medium-grey">Filters loading...</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-servify-light-grey shadow-lg">
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-12 w-12 bg-servify-light-grey rounded-lg mb-4"></div>
                  <div className="h-8 bg-servify-light-grey rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-servify-light-grey rounded w-3/4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="analytics">
        <div>
          <h1 className="text-3xl font-bold text-servify-dark-grey">Analytics Dashboard</h1>
          <p className="text-servify-medium-grey text-servify-purple">Error loading analytics: {error.message}</p>
        </div>
      </div>
    );
  }

  // Process detailed analytics for enhanced insights
  // Extract available departments for filtering
  const availableDepartments: string[] = detailedAnalyticsData && Array.isArray(detailedAnalyticsData)
    ? Array.from(new Set(detailedAnalyticsData
        .map((user: any) => user.department)
        .filter((dept: any) => dept)
      )).sort() as string[]
    : [];

  const processedUserAnalytics = detailedAnalyticsData && Array.isArray(detailedAnalyticsData) ? (() => {
    let filteredUsers = detailedAnalyticsData;

    // Apply search filter
    if (userSearchTerm) {
      filteredUsers = filteredUsers.filter((user: any) => 
        user.userName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.userEmail.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }

    // Apply department filter
    if (departmentFilter !== 'all') {
      filteredUsers = filteredUsers.filter((user: any) => 
        user.department === departmentFilter
      );
    }

    // Apply performance filter
    if (performanceFilter !== 'all') {
      filteredUsers = filteredUsers.filter((user: any) => {
        const combinedScore = (user.overallProgress.completionPercentage + user.overallProgress.averageQuizScore) / 2;
        switch (performanceFilter) {
          case 'excellent': return combinedScore >= 85;
          case 'good': return combinedScore >= 70 && combinedScore < 85;
          case 'average': return combinedScore >= 50 && combinedScore < 70;
          case 'needs-help': return combinedScore < 50;
          default: return true;
        }
      });
    }

    // Apply progress filter
    if (progressFilter !== 'all') {
      filteredUsers = filteredUsers.filter((user: any) => {
        const progress = user.overallProgress.completionPercentage;
        switch (progressFilter) {
          case 'completed': return progress === 100;
          case 'high': return progress >= 80 && progress < 100;
          case 'medium': return progress >= 50 && progress < 80;
          case 'low': return progress > 0 && progress < 50;
          case 'not-started': return progress === 0;
          default: return true;
        }
      });
    }

    // Apply quiz filter
    if (quizFilter !== 'all') {
      filteredUsers = filteredUsers.filter((user: any) => {
        const quizScore = user.overallProgress.averageQuizScore;
        const hasAttempts = user.overallProgress.totalQuizAttempts > 0;
        
        switch (quizFilter) {
          case 'excellent': return quizScore >= 90;
          case 'good': return quizScore >= 75 && quizScore < 90;
          case 'passing': return quizScore >= 60 && quizScore < 75;
          case 'below-passing': return quizScore < 60 && hasAttempts;
          case 'no-attempts': return !hasAttempts;
          default: return true;
        }
      });
    }

    return {
      // Return filtered users
      filteredUsers: filteredUsers
    };
  })() : null;

  // Analytics data processed successfully (safe logging without PII)
  const analytics = analyticsData || {
    totalUsers: 0,
    avgCompletionTime: "N/A",
    completionRate: 0,
    activeModules: 0,
    totalProgress: 0,
    totalQuizzes: 0,
    totalQuizAttempts: 0,
    avgQuizScore: 0,
    recentActivity: [],
    // Welcome to Servify Analytics
    welcomeModuleLoginRate: [],
    averageEngagementTime: { dailyAverage: 0, weeklyAverage: 0, monthlyAverage: 0 },
    // Knowledge Hub Analytics
    moduleCompletionRate: [],
    quizScoreAverage: {
      overallAverage: 0,
      moduleWiseAverage: []
    },
    // Legacy fallback data
    moduleCompletionData: [],
    userEngagementData: [],
    completionRateData: [],
    weeklyProgressData: []
  };

  // Function to export Knowledge Hub analytics to Excel
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      
      // Fetch detailed user analytics
      const params = new URLSearchParams();
      if (filters.customDateRange?.from && filters.customDateRange?.to) {
        params.append('startDate', filters.customDateRange.from.toISOString());
        params.append('endDate', filters.customDateRange.to.toISOString());
      } else {
        params.append('dateRange', filters.dateRange.toString());
      }
      if (filters.department) params.append('department', filters.department);

      // Add cache-busting parameter to avoid 304 cached responses
      params.append('_t', Date.now().toString());
      
      const response = await apiRequest("GET", `/analytics/detailed?${params}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to fetch detailed analytics');
      }

      const result = await response.json();
      console.log('Export response structure:', { 
        hasData: !!result.data, 
        dataType: typeof result.data,
        isArray: Array.isArray(result.data),
        dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
        resultKeys: Object.keys(result),
        fullResult: result
      });
      
      // Handle different response structures
      let detailedData = result.data;
      
      // If data is not directly in result.data, check other possible locations
      if (!detailedData && Array.isArray(result)) {
        detailedData = result;
      } else if (!detailedData && result.analytics && Array.isArray(result.analytics)) {
        detailedData = result.analytics;
      } else if (result.data && typeof result.data === 'object' && !Array.isArray(result.data)) {
        // If data is an object but not an array, it might be wrapped
        console.warn('Data is an object, not an array. Attempting to extract array...', result.data);
        // Check if it has a nested data property
        if (result.data.data && Array.isArray(result.data.data)) {
          detailedData = result.data.data;
        } else if (result.data.users && Array.isArray(result.data.users)) {
          detailedData = result.data.users;
        }
      }
      
      if (!detailedData || !Array.isArray(detailedData)) {
        console.error('Invalid response structure:', {
          result,
          resultType: typeof result,
          resultKeys: Object.keys(result),
          dataType: typeof result.data,
          dataValue: result.data
        });
        throw new Error(`Invalid data format received from server. Expected array, got: ${typeof detailedData}. Response keys: ${Object.keys(result).join(', ')}`);
      }
      
      if (detailedData.length === 0) {
        toast({
          title: "No Data Available",
          description: "There is no data to export for the selected filters.",
          variant: "destructive",
        });
        return;
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Sheet 1: User Summary
      const userSummaryData = detailedData.map((user: any) => ({
        'User Name': user.userName,
        'Email': user.userEmail,
        'Role': user.role,
        'Department': user.department || 'N/A',
        'Designation': user.designation || 'N/A',
        'Join Date': user.joinDate ? formatDateStandard(user.joinDate) : 'N/A',
        'Total Modules': user.overallProgress.totalModules,
        'Completed Modules': user.overallProgress.completedModules,
        'Completion %': user.overallProgress.completionPercentage,
        'Total Quizzes': user.overallProgress.totalQuizzes,
        'Passed Quizzes': user.overallProgress.passedQuizzes,
        'Average Quiz Score': user.overallProgress.averageQuizScore
      }));
      const userSummarySheet = XLSX.utils.json_to_sheet(userSummaryData);
      XLSX.utils.book_append_sheet(workbook, userSummarySheet, 'User Summary');

      // Sheet 2: Module Completions (detailed)
      const moduleCompletionData: any[] = [];
      detailedData.forEach((user: any) => {
        user.moduleCompletions.forEach((module: any) => {
          moduleCompletionData.push({
            'User Name': user.userName,
            'Email': user.userEmail,
            'Module Title': module.moduleTitle,
            'Day': module.moduleDay,
            'Sequence': module.moduleSequence,
            'Completed': module.isCompleted ? 'Yes' : 'No',
            'Completed At': module.completedAt ? new Date(module.completedAt).toLocaleString() : 'Not Completed',
            'Time Spent (seconds)': module.timeSpent || 0
          });
        });
      });
      const moduleCompletionSheet = XLSX.utils.json_to_sheet(moduleCompletionData);
      XLSX.utils.book_append_sheet(workbook, moduleCompletionSheet, 'Module Details');

      // Sheet 3: Quiz Performance (detailed)
      const quizPerformanceData: any[] = [];
      detailedData.forEach((user: any) => {
        user.quizPerformance.forEach((quiz: any) => {
          quizPerformanceData.push({
            'User Name': user.userName,
            'Email': user.userEmail,
            'Quiz Title': quiz.quizTitle,
            'Module Title': quiz.moduleTitle,
            'Score': quiz.score,
            'Passed': quiz.isPassed ? 'Yes' : 'No',
            'Passing Score': quiz.passingScore,
            'Attempted At': new Date(quiz.attemptedAt).toLocaleString()
          });
        });
      });
      
      if (quizPerformanceData.length > 0) {
        const quizPerformanceSheet = XLSX.utils.json_to_sheet(quizPerformanceData);
        XLSX.utils.book_append_sheet(workbook, quizPerformanceSheet, 'Quiz Performance');
      }

      // Sheet 4: Analytics Summary
      const analyticsSummaryData = [
        { 'Metric': 'Total Users', 'Value': analytics.totalUsers },
        { 'Metric': 'Active Modules', 'Value': analytics.activeModules },
        { 'Metric': 'Overall Completion Rate', 'Value': `${analytics.completionRate}%` },
        { 'Metric': 'Total Quizzes', 'Value': analytics.totalQuizzes },
        { 'Metric': 'Total Quiz Attempts', 'Value': analytics.totalQuizAttempts },
        { 'Metric': 'Average Quiz Score', 'Value': `${analytics.avgQuizScore}%` },
        { 'Metric': 'Overall Quiz Average', 'Value': `${analytics.quizScoreAverage.overallAverage}%` }
      ];
      
      // Add module completion rates
      analytics.moduleCompletionRate.forEach((module: any) => {
        analyticsSummaryData.push({
          'Metric': `${module.module} Completion Rate`,
          'Value': `${module.completionPercentage}% (${module.completed}/${module.total})`
        });
      });

      // Add quiz averages by module
      analytics.quizScoreAverage.moduleWiseAverage.forEach((module: any) => {
        analyticsSummaryData.push({
          'Metric': `${module.moduleTitle} Quiz Average`,
          'Value': `${module.averageScore}% (${module.totalAttempts} attempts, ${module.passingRate}% pass rate)`
        });
      });

      const analyticsSummarySheet = XLSX.utils.json_to_sheet(analyticsSummaryData);
      XLSX.utils.book_append_sheet(workbook, analyticsSummarySheet, 'Analytics Summary');

      // Generate filename with current date and filters
      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = filters.customDateRange 
        ? `${filters.customDateRange.from?.toISOString().split('T')[0]}_to_${filters.customDateRange.to?.toISOString().split('T')[0]}`
        : `last_${filters.dateRange}_days`;
      const filename = `Knowledge_Hub_Analytics_${filterStr}_${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);
      
      toast({
        title: "Export Successful",
        description: `Knowledge Hub analytics exported successfully as ${filename}`,
      });
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data to Excel. Please try again.';
      toast({
        title: "Export Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="space-y-8" data-testid="analytics">
      <div>
        <h1 className="text-3xl font-bold text-servify-dark-grey">Analytics Dashboard</h1>
        <p className="text-servify-medium-grey">Comprehensive insights into candidate and employee engagement</p>
      </div>

      {/* Filter Controls */}
      <Card className="border-servify-light-grey" data-testid="analytics-filters">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-servify-purple" />
            Analytics Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            {/* Preset Date Options */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Quick Date Range</label>
              <Select 
                value={(() => {
                  // If custom picker is shown, return "custom"
                  if (showCustomPicker) {
                    return "custom";
                  }
                  
                  // Check if custom date range matches preset options
                  if (filters.customDateRange?.from && filters.customDateRange?.to) {
                    const today = new Date();
                    const yesterday = subDays(today, 1);
                    const thisWeekStart = startOfWeek(today);
                    const thisWeekEnd = endOfWeek(today);
                    const thisMonthStart = startOfMonth(today);
                    const thisMonthEnd = endOfMonth(today);
                    
                    // Check if it matches "Today"
                    if (format(filters.customDateRange.from, 'yyyy-MM-dd') === format(startOfDay(today), 'yyyy-MM-dd') &&
                        format(filters.customDateRange.to, 'yyyy-MM-dd') === format(endOfDay(today), 'yyyy-MM-dd')) {
                      return "today";
                    }
                    
                    // Check if it matches "Yesterday"
                    if (format(filters.customDateRange.from, 'yyyy-MM-dd') === format(startOfDay(yesterday), 'yyyy-MM-dd') &&
                        format(filters.customDateRange.to, 'yyyy-MM-dd') === format(endOfDay(yesterday), 'yyyy-MM-dd')) {
                      return "yesterday";
                    }
                    
                    // Check if it matches "This Week"
                    if (format(filters.customDateRange.from, 'yyyy-MM-dd') === format(thisWeekStart, 'yyyy-MM-dd') &&
                        format(filters.customDateRange.to, 'yyyy-MM-dd') === format(thisWeekEnd, 'yyyy-MM-dd')) {
                      return "thisWeek";
                    }
                    
                    // Check if it matches "This Month"
                    if (format(filters.customDateRange.from, 'yyyy-MM-dd') === format(thisMonthStart, 'yyyy-MM-dd') &&
                        format(filters.customDateRange.to, 'yyyy-MM-dd') === format(thisMonthEnd, 'yyyy-MM-dd')) {
                      return "thisMonth";
                    }
                    
                    // If it's a custom range that doesn't match any preset
                    return "custom";
                  }
                  
                  // Return the preset date range value
                  return filters.dateRange.toString();
                })()} 
                onValueChange={(value) => {
                  if (value === "custom") {
                    // Show custom picker and keep current custom date range
                    setShowCustomPicker(true);
                    return;
                  } else if (value === "today") {
                    const today = new Date();
                    setFilters({
                      ...filters, 
                      dateRange: 1,
                      customDateRange: { from: startOfDay(today), to: endOfDay(today) }
                    });
                    setShowCustomPicker(false);
                  } else if (value === "yesterday") {
                    const yesterday = subDays(new Date(), 1);
                    setFilters({
                      ...filters, 
                      dateRange: 1,
                      customDateRange: { from: startOfDay(yesterday), to: endOfDay(yesterday) }
                    });
                    setShowCustomPicker(false);
                  } else if (value === "thisWeek") {
                    const now = new Date();
                    setFilters({
                      ...filters, 
                      dateRange: 7,
                      customDateRange: { from: startOfWeek(now), to: endOfWeek(now) }
                    });
                    setShowCustomPicker(false);
                  } else if (value === "thisMonth") {
                    const now = new Date();
                    setFilters({
                      ...filters, 
                      dateRange: 30,
                      customDateRange: { from: startOfMonth(now), to: endOfMonth(now) }
                    });
                    setShowCustomPicker(false);
                  } else {
                    setFilters({
                      ...filters, 
                      dateRange: parseInt(value),
                      customDateRange: undefined
                    });
                    setShowCustomPicker(false);
                  }
                }}
              >
                <SelectTrigger className="w-[160px]" data-testid="filter-date-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="thisWeek">This Week</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range Picker */}
            {showCustomPicker && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Custom Date Range</label>
                <DateRangePicker
                  dateRange={filters.customDateRange}
                  onDateRangeChange={(dateRange) => {
                    setFilters({
                      ...filters,
                      customDateRange: dateRange,
                      dateRange: dateRange ? 0 : filters.dateRange // Set to 0 when using custom range
                    });
                  }}
                  className="w-[300px]"
                />
              </div>
            )}
          </div>
          
          <div className="flex items-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setFilters({
                  dateRange: 30, 
                  customDateRange: undefined,
                  department: undefined
                });
                setShowCustomPicker(false);
              }}
              className="text-gray-600 hover:text-gray-800"
              data-testid="button-reset-filters"
            >
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Welcome to Servify Candidate and Employee Section */}
      <div className="space-y-6">
        <div className="border-l-4 border-servify-purple pl-4">
          <h2 className="text-2xl font-bold text-servify-dark-grey mb-2">Welcome to Servify Candidate and Employee</h2>
          <p className="text-servify-medium-grey">Track login rates and engagement times for new joiners</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Login Rate */}
          <Card className="border-servify-light-grey shadow-lg" data-testid="login-rate-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Login Rate</CardTitle>
              <LogIn className="h-4 w-4 text-servify-purple" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Candidate Login Rate */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-servify-alt-green">Candidates</span>
                    <div className="text-xl font-bold text-servify-alt-green">
                      {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'candidate')?.loginPercentage || 0}%
                    </div>
                  </div>
                  <p className="text-xs text-servify-alt-green">
                    {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'candidate')?.usersAccessed || 0} of {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'candidate')?.totalUsers || 0} candidates accessed welcome module
                  </p>
                </div>

                {/* Employee Login Rate */}
                <div className="p-3 bg-servify-purple/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-servify-purple">Employees</span>
                    <div className="text-xl font-bold text-servify-purple">
                      {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'employee')?.loginPercentage || 0}%
                    </div>
                  </div>
                  <p className="text-xs text-servify-purple">
                    {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'employee')?.usersAccessed || 0} of {analytics.welcomeModuleLoginRate?.find((item: WelcomeModuleLoginRate) => item.userType === 'employee')?.totalUsers || 0} employees accessed welcome module
                  </p>
                </div>

                {/* Comparison Chart */}
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.welcomeModuleLoginRate || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="userType" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value: any, name: any) => [`${value}%`, 'Login Rate']}
                        labelFormatter={(label) => `${label.charAt(0).toUpperCase() + label.slice(1)}`}
                      />
                      <Bar dataKey="loginPercentage">
                        {(analytics.welcomeModuleLoginRate || []).map((entry: WelcomeModuleLoginRate, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.userType === 'candidate' ? 'var(--servify-alt-green)' : 'var(--servify-purple)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Time */}
          <Card className="border-servify-light-grey shadow-lg" data-testid="engagement-time-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engagement Time</CardTitle>
              <Timer className="h-4 w-4 text-servify-purple" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-servify-purple mb-2">
                {analytics.averageEngagementTime?.dailyAverage || 0} min
              </div>
              <p className="text-xs text-servify-medium-grey mb-4">Average daily engagement</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-servify-medium-grey">Daily</span>
                  <span className="text-sm font-medium">{analytics.averageEngagementTime?.dailyAverage || 0} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-servify-medium-grey">Weekly</span>
                  <span className="text-sm font-medium">{analytics.averageEngagementTime?.weeklyAverage || 0} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-servify-medium-grey">Monthly</span>
                  <span className="text-sm font-medium">{analytics.averageEngagementTime?.monthlyAverage || 0} min</span>
                </div>
              </div>
            </CardContent>
          </Card>


        </div>
      </div>

      {/* Enhanced Analytics View Toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-lg border border-servify-light-grey">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-servify-dark-grey">Analytics View:</h3>
          <div className="flex gap-2">
            <Button
              variant={selectedView === 'overview' ? 'default' : 'outline'}
              onClick={() => setSelectedView('overview')}
              className={selectedView === 'overview' ? 'bg-servify-green hover:bg-servify-green/90' : ''}
              data-testid="button-view-overview"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Overview
            </Button>
            <Button
              variant={selectedView === 'user-details' ? 'default' : 'outline'}
              onClick={() => setSelectedView('user-details')}
              className={selectedView === 'user-details' ? 'bg-servify-green hover:bg-servify-green/90' : ''}
              data-testid="button-view-user-details"
            >
              <Eye className="h-4 w-4 mr-2" />
              User Details
            </Button>
          </div>
        </div>
        
      </div>


      {/* User Search and Analytics Table */}
      {selectedView === 'user-details' && processedUserAnalytics && (
        <Card className="border-servify-light-grey shadow-lg" data-testid="user-details-table">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-servify-green" />
                Individual User Progress
              </div>
              <span className="text-sm text-servify-medium-grey">
                {processedUserAnalytics.filteredUsers.length} users found
              </span>
            </CardTitle>
            
            {/* Enhanced Filters */}
            <div className="space-y-4 pt-4">
              {/* Search Bar */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-servify-medium-grey" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-10 border-servify-light-grey focus:ring-2 focus:ring-servify-purple focus:border-servify-purple"
                    data-testid="input-user-search"
                  />
                </div>
                {userSearchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUserSearchTerm('')}
                    className="text-servify-medium-grey hover:text-servify-dark-grey"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {/* Filter Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Performance Level Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-servify-dark-grey">Performance Level</label>
                  <Select value={performanceFilter} onValueChange={setPerformanceFilter}>
                    <SelectTrigger className="border-servify-light-grey" data-testid="select-performance">
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="excellent">Excellent (85%+)</SelectItem>
                      <SelectItem value="good">Good (70-84%)</SelectItem>
                      <SelectItem value="average">Average (50-69%)</SelectItem>
                      <SelectItem value="needs-help">Needs Help (&lt;50%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Progress Range Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-servify-dark-grey">Module Progress</label>
                  <Select value={progressFilter} onValueChange={setProgressFilter}>
                    <SelectTrigger className="border-servify-light-grey" data-testid="select-progress">
                      <SelectValue placeholder="All Progress" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="all">All Progress</SelectItem>
                      <SelectItem value="completed">Completed (100%)</SelectItem>
                      <SelectItem value="high">High Progress (80%+)</SelectItem>
                      <SelectItem value="medium">Medium Progress (50-79%)</SelectItem>
                      <SelectItem value="low">Low Progress (&lt;50%)</SelectItem>
                      <SelectItem value="not-started">Not Started (0%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quiz Score Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-servify-dark-grey">Quiz Performance</label>
                  <Select value={quizFilter} onValueChange={setQuizFilter}>
                    <SelectTrigger className="border-servify-light-grey" data-testid="select-quiz">
                      <SelectValue placeholder="All Scores" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="all">All Scores</SelectItem>
                      <SelectItem value="excellent">Excellent (90%+)</SelectItem>
                      <SelectItem value="good">Good (75-89%)</SelectItem>
                      <SelectItem value="passing">Passing (60-74%)</SelectItem>
                      <SelectItem value="below-passing">Below Passing (&lt;60%)</SelectItem>
                      <SelectItem value="no-attempts">No Attempts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Department Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-servify-dark-grey">Department</label>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="border-servify-light-grey" data-testid="select-department">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="all">All Departments</SelectItem>
                      {availableDepartments.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Clear All Filters */}
              {(performanceFilter !== 'all' || progressFilter !== 'all' || quizFilter !== 'all' || departmentFilter !== 'all') && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPerformanceFilter('all');
                      setProgressFilter('all');
                      setQuizFilter('all');
                      setDepartmentFilter('all');
                    }}
                    className="text-servify-medium-grey hover:text-servify-dark-grey"
                    data-testid="button-clear-filters"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingDetailed ? (
              <div className="text-center py-8">
                <div className="text-servify-medium-grey">Loading detailed user analytics...</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-servify-light-grey">
                      <th className="text-left p-3 font-semibold text-servify-dark-grey">User</th>
                      <th className="text-center p-3 font-semibold text-servify-dark-grey">Overall Progress</th>
                      <th className="text-center p-3 font-semibold text-servify-dark-grey">Modules Completed</th>
                      <th className="text-center p-3 font-semibold text-servify-dark-grey">Quiz Average</th>
                      <th className="text-center p-3 font-semibold text-servify-dark-grey">Quiz Attempts</th>
                      <th className="text-center p-3 font-semibold text-servify-dark-grey">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedUserAnalytics.filteredUsers.map((user: any, index: number) => {
                      const combinedScore = (user.overallProgress.completionPercentage + user.overallProgress.averageQuizScore) / 2;
                      const getStatusColor = () => {
                        if (combinedScore >= 85) return 'bg-green-100 text-green-800';
                        if (combinedScore >= 70) return 'bg-blue-100 text-blue-800';
                        if (combinedScore >= 50) return 'bg-yellow-100 text-yellow-800';
                        return 'bg-red-100 text-red-800';
                      };
                      const getStatusText = () => {
                        if (combinedScore >= 85) return 'Excellent';
                        if (combinedScore >= 70) return 'Good';
                        if (combinedScore >= 50) return 'Average';
                        return 'Needs Help';
                      };
                      
                      return (
                        <tr key={index} className="border-b border-servify-light-grey hover:bg-servify-white-grey">
                          <td className="p-3">
                            <div>
                              <div className="font-medium text-servify-dark-grey">{user.userName}</div>
                              <div className="text-xs text-servify-medium-grey">{user.userEmail}</div>
                              {user.department && (
                                <div className="text-xs text-servify-medium-grey mt-1">
                                  <Badge variant="outline" className="text-xs">{user.department}</Badge>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex flex-col items-center">
                              <div className="font-bold text-lg text-servify-green">
                                {user.overallProgress.completionPercentage}%
                              </div>
                              <div className="w-16 bg-servify-light-grey rounded-full h-1.5 mt-1">
                                <div 
                                  className="h-1.5 rounded-full bg-servify-green"
                                  style={{ width: `${user.overallProgress.completionPercentage}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="font-medium">
                              {user.overallProgress.modulesCompleted}/{user.overallProgress.totalModules}
                            </div>
                            <div className="text-xs text-servify-medium-grey">modules</div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="font-bold text-servify-alt-green">
                              {user.overallProgress.averageQuizScore}%
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="font-medium">{user.overallProgress.totalQuizAttempts}</div>
                            <div className="text-xs text-servify-medium-grey">attempts</div>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={`${getStatusColor()} border-0 font-medium`}>
                              {getStatusText()}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {processedUserAnalytics.filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-servify-medium-grey">
                    No users found matching your search criteria.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Knowledge Hub Section */}
      {selectedView === 'overview' && (
        <div className="space-y-6">
          <div className="border-l-4 border-servify-green pl-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-servify-dark-grey mb-2">Knowledge Hub (Modules + Policies + Quizzes)</h2>
                <p className="text-servify-medium-grey">Track module completion rates and quiz performance across all training content</p>
              </div>
            <Button
              onClick={exportToExcel}
              disabled={isExporting}
              className="bg-servify-green hover:bg-servify-green/90 text-white"
              data-testid="button-export-excel"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Module Completion Rate */}
          <Card className="border-servify-light-grey shadow-lg" data-testid="module-completion-rate-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg font-medium">Module Completion Rate</CardTitle>
              <GraduationCap className="h-5 w-5 text-servify-green" />
            </CardHeader>
            <CardContent>
              {/* Module Completion Cards */}
              <div className="space-y-3 mb-6">
                {analytics.moduleCompletionRate?.map((module: ModuleCompletion, index: number) => {
                  // Create shorter display names
                  const getShortName = (fullName: string) => {
                    switch(fullName) {
                      case 'Welcome to Servify': return 'Welcome';
                      case 'Code of Conduct': return 'Code of Conduct';
                      case 'First Impressions': return 'First Impressions';
                      case 'Refer a Buddy': return 'Refer a Buddy';
                      default: return fullName;
                    }
                  };
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-servify-dark-grey">{getShortName(module.module)}</h4>
                        <p className="text-sm text-servify-medium-grey">
                          {module.completed} of {module.total} users completed
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-servify-light-grey rounded-full h-2 min-w-[100px]">
                          <div 
                            className="h-2 rounded-full transition-all duration-300" 
                            style={{ 
                              width: `${module.completionPercentage}%`,
                              backgroundColor: 'var(--servify-alt-green)'
                            }}
                          />
                        </div>
                        <span className="font-bold text-lg min-w-[50px] text-right text-servify-alt-green">
                          {module.completionPercentage}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Summary Chart */}
              <div className="h-48 mt-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={analytics.moduleCompletionRate?.map((item: ModuleCompletion) => ({
                      ...item,
                      shortName: (() => {
                        switch(item.module) {
                          case 'Welcome to Servify': return 'Welcome';
                          case 'Code of Conduct': return 'Code';
                          case 'First Impressions': return 'Impressions';
                          case 'Refer a Buddy': return 'Referral';
                          default: return item.module;
                        }
                      })()
                    })) || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--servify-light-grey)" />
                    <XAxis 
                      dataKey="shortName" 
                      fontSize={12}
                      tick={{ fill: 'var(--servify-medium-grey)' }}
                    />
                    <YAxis 
                      label={{ value: 'Completion %', angle: -90, position: 'insideLeft' }}
                      fontSize={12}
                      tick={{ fill: 'var(--servify-medium-grey)' }}
                    />
                    <Tooltip 
                      formatter={(value: any, name: any) => [`${value}%`, 'Completion Rate']}
                      labelFormatter={(label: string) => {
                        const fullNames: { [key: string]: string } = {
                          'Welcome': 'Welcome to Servify',
                          'Code': 'Code of Conduct', 
                          'Impressions': 'First Impressions',
                          'Referral': 'Refer a Buddy'
                        };
                        return fullNames[label] || label;
                      }}
                      contentStyle={{ 
                        backgroundColor: 'white',
                        border: '1px solid var(--servify-light-grey)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="completionPercentage" 
                      fill="var(--servify-alt-green)"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="mt-4">
                <p className="text-sm text-servify-medium-grey">
                  Track completion rates across all training modules with detailed progress indicators
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

    </div>
  );
}